/**
 * Shape of a GetNotifications row plus the unwrapping the endpoint needs.
 * The gateway returns the array at one of several depths depending on the
 * wrapper, so every caller resolves it through `extractNotificationRows`.
 */
import type { NotificationItem } from '@wlb/shared/types/notification';
import type { PendingApprovalRequest } from '@wlb/shared/types/groupDetails';
import type { ApiError } from '@wlb/shared/types/api';

/**
 * A single GetNotifications row. The endpoint carries no `type` field — the
 * kind is derived from `notificationTypeName` by `resolveNotificationKind`.
 */
export interface NotificationRow {
  notificationId?: number;
  NotificationId?: number | string;
  notificationID?: number | string;
  NotificationID?: number | string;
  id?: string | number;
  type?: string;
  title?: string;
  notificationTypeName?: string;
  message?: string;
  createdAt?: string;
  time?: string;
  section?: string;
  isRead?: boolean;
  status?: string;
  actorProfilePicture?: string;
  ActorProfilePicture?: string;
  actorProfilePictureUrl?: string;
  profilePicture?: string;
  ProfilePicture?: string;
  profilePictureUrl?: string;
  ProfilePictureUrl?: string;
  userPhoto?: string;
  senderAvatarUrl?: string;
  actorUserId?: number | string;
  requestedByUserId?: number | string;
  actorUserName?: string;
  actorName?: string;
  requestedByUserName?: string;
  senderName?: string;
  senderUserName?: string;
  createdByName?: string;
  fromUserName?: string;
  userName?: string;
  actorUserGuid?: string;
  ActorUserGuid?: string;
  actorProfileUserGuid?: string;
  senderUserGuid?: string;
  createdByUserGuid?: string;
  followeeUserGuid?: string;
  FolloweeUserGuid?: string;
  profileGuid?: string;
  userGuid?: string;
  UserGuid?: string;
  profileUserGuid?: string;
  ProfileUserGuid?: string;
  senderId?: number | string;
  postId?: number;
  postCommentId?: number;
  groupId?: number | string;
  GroupId?: number | string;
  groupJoinRequestId?: number | string;
  joinRequestId?: number | string;
  GroupJoinRequestId?: number | string;
  JoinRequestId?: number | string;
  conversationId?: number;
  messageId?: number;
  user?: { name?: string; avatar?: string | { uri: string } | null };
}

/** Followee-post rows use the WLB Buddy mark rather than a person avatar. */
export const isFolloweeNewPostNotification = (
  notification: Pick<NotificationRow, 'notificationTypeName' | 'title'>,
): boolean => {
  const label = (notification.notificationTypeName || notification.title || '')
    .trim()
    .toLowerCase();
  return label === 'followee new post';
};

export const getPositiveApiId = (...candidates: unknown[]): number | null => {
  for (const candidate of candidates) {
    if (candidate == null || candidate === '') continue;
    const numericId = Number(candidate);
    if (Number.isSafeInteger(numericId) && numericId > 0) return numericId;
  }
  return null;
};

export const getNotificationRowApiId = (
  notification: Pick<
    NotificationRow,
    'notificationId' | 'NotificationId' | 'notificationID' | 'NotificationID' | 'id'
  >,
): number | null =>
  getPositiveApiId(
    notification.notificationId,
    notification.NotificationId,
    notification.notificationID,
    notification.NotificationID,
    notification.id,
  );

export const getNotificationActorName = (
  notification: Pick<
    NotificationRow,
    | 'actorUserName'
    | 'actorName'
    | 'requestedByUserName'
    | 'senderName'
    | 'senderUserName'
    | 'createdByName'
    | 'fromUserName'
    | 'userName'
    | 'message'
    | 'user'
  >,
): string => {
  const messageActor = notification.message
    ?.match(
      /^\s*(.+?)\s+(?:requested\s+to\s+join|added\s+(?:a\s+)?new\s+post|added\s+you\s+to|started\s+a\s+chat\s+with\s+you|posted\s+in|liked\b|commented\b|replied\b|mentioned\b|reacted\b|sent\s+you\s+(?:a\s+)?buddy\s+request|accepted\s+your\s+buddy\s+request|started\s+following\b)/i,
    )?.[1]
    ?.trim();
  const candidates = [
    notification.actorUserName,
    notification.actorName,
    notification.requestedByUserName,
    notification.senderName,
    notification.senderUserName,
    notification.createdByName,
    notification.fromUserName,
    notification.userName,
    notification.user?.name,
    messageActor,
  ];

  return candidates.find(candidate => candidate?.trim())?.trim() ?? '';
};

export const getNotificationActorGuid = (
  notification: Pick<
    NotificationRow,
    | 'actorUserGuid'
    | 'ActorUserGuid'
    | 'actorProfileUserGuid'
    | 'senderUserGuid'
    | 'createdByUserGuid'
    | 'followeeUserGuid'
    | 'FolloweeUserGuid'
    | 'profileUserGuid'
    | 'ProfileUserGuid'
    | 'profileGuid'
    | 'userGuid'
    | 'UserGuid'
  >,
): string | undefined => {
  const candidates = [
    notification.actorUserGuid,
    notification.ActorUserGuid,
    notification.actorProfileUserGuid,
    notification.senderUserGuid,
    notification.createdByUserGuid,
    notification.followeeUserGuid,
    notification.FolloweeUserGuid,
    notification.profileUserGuid,
    notification.ProfileUserGuid,
    notification.profileGuid,
    notification.userGuid,
    notification.UserGuid,
  ];

  return candidates.find(candidate => candidate?.trim())?.trim();
};

export const getNotificationActorImage = (
  notification: Pick<
    NotificationRow,
    | 'actorProfilePicture'
    | 'ActorProfilePicture'
    | 'actorProfilePictureUrl'
    | 'profilePicture'
    | 'ProfilePicture'
    | 'profilePictureUrl'
    | 'ProfilePictureUrl'
    | 'userPhoto'
    | 'senderAvatarUrl'
  >,
): string | undefined => {
  const candidates = [
    notification.actorProfilePicture,
    notification.ActorProfilePicture,
    notification.actorProfilePictureUrl,
    notification.profilePicture,
    notification.ProfilePicture,
    notification.profilePictureUrl,
    notification.ProfilePictureUrl,
    notification.userPhoto,
    notification.senderAvatarUrl,
  ];

  return candidates.find(candidate => candidate?.trim())?.trim();
};

/** First match wins, so the shallowest envelope is listed first. */
const ROW_PATHS: readonly (readonly string[])[] = [
  ['notifications'],
  ['data', 'notifications'],
  ['data', 'data'],
  ['data', 'data', 'list'],
  ['data', 'data', 'items'],
];

const readPath = (source: unknown, path: readonly string[]): unknown =>
  path.reduce<unknown>(
    (value, key) =>
      value && typeof value === 'object'
        ? (value as Record<string, unknown>)[key]
        : undefined,
    source,
  );

export const extractNotificationRows = (response: unknown): NotificationRow[] => {
  for (const path of ROW_PATHS) {
    const value = readPath(response, path);
    if (Array.isArray(value)) return value as NotificationRow[];
  }
  return [];
};

/**
 * Returns the real API id for row actions. Locally generated display ids must
 * never be sent to the delete endpoint.
 */
export const getNotificationApiId = (
  notification: Pick<NotificationItem, 'id' | 'notificationId'>,
): number | null => {
  const candidate = notification.notificationId
    ?? (/^\d+$/.test(notification.id) ? Number(notification.id) : null);

  return typeof candidate === 'number'
    && Number.isSafeInteger(candidate)
    && candidate > 0
    ? candidate
    : null;
};

export const matchesGroupJoinRequest = (
  notification: Pick<
    NotificationItem,
    'groupJoinRequestId' | 'actorUserGuid' | 'actorUserId' | 'senderId' | 'user'
  >,
  request: PendingApprovalRequest,
): boolean => {
  if (
    notification.groupJoinRequestId != null
    && request.groupJoinRequestId === notification.groupJoinRequestId
  ) {
    return true;
  }

  const actorGuid = notification.actorUserGuid?.trim().toLowerCase();
  const requestGuid = request.profileUserGuid?.trim().toLowerCase();
  if (actorGuid && requestGuid && actorGuid === requestGuid) return true;

  const actorUserId = notification.actorUserId ?? notification.senderId;
  if (actorUserId != null && request.requestedByUserId === actorUserId) return true;

  const actorName = notification.user?.name?.trim().toLowerCase();
  return Boolean(actorName && request.name.trim().toLowerCase() === actorName);
};

export const isResolvedGroupJoinRequestError = (caught: unknown): boolean => {
  const error = caught as ApiError;
  const status = error?.response?.status;
  if (status === 404 || status === 409) return true;

  const body = error?.response?.data;
  const apiMessage = typeof body === 'string' ? body : body?.message;
  const message = (apiMessage || (caught as Error)?.message || '').toLowerCase();
  return [
    'already approved',
    'already rejected',
    'already reviewed',
    'already handled',
    'not found',
    'no longer available',
  ].some(phrase => message.includes(phrase));
};

export const getGroupJoinRequestApiId = (
  notification: Pick<
    NotificationRow,
    'groupJoinRequestId' | 'joinRequestId' | 'GroupJoinRequestId' | 'JoinRequestId'
  >,
): number | null => {
  const candidates = [
    notification.groupJoinRequestId,
    notification.joinRequestId,
    notification.GroupJoinRequestId,
    notification.JoinRequestId,
  ];
  return getPositiveApiId(...candidates);
};

/** GetNotifications is unpaginated on web — one page covers the bell list. */
export const NOTIFICATIONS_PAGE_SIZE = 100;
